import cn from 'classnames';
import { format } from 'date-fns';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { CartesianGrid, ComposedChart, ReferenceArea, Tooltip, XAxis, YAxis } from 'recharts';
import { ScaleType } from 'recharts/types/util/types';
import {
  BpmDateString,
  dateStringToUtcTimestamp
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import {
  ForecastKpiViewOption,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES,
  OutcomeVariableType
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { convertDateToUTC } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { ShadedPattern } from '@Components/FillPatterns/ShadedPattern';
import { CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from '../../../Components/Charts/ChartConstants';
import { buildEquidistantScale, useDynamicYAxisWidth } from '../../../Components/Charts/ChartUtils';
import { useBuildMonthlyXAxis } from '../../../Components/Charts/MonthlyXAxis/MonthlyXAxis';
import { Brand50, Brand70, getColorMap, Neutral500 } from '../../../utils/colors';
import { dateToMddyy, parseEpochDate } from '../../../utils/date-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { useContainerSize } from '../../../utils/hooks/useContainerSize';
import {
  FlattenedForecastDataResult,
  ForecastDataTimestampRanges,
  formatAxisSpend,
  formatCpm,
  formatCpmRounded,
  HISTORICAL_KEY
} from '../forecasting-constants';
import { formatForecastViewLabel, getKpiAxisFormatter } from '../forecasting-utils';
import {
  buildKpiComparisonGraphs,
  buildKpiPrimaryOnlyGraphs,
  buildSpendComparisonGraphs,
  buildSpendPrimaryOnlyGraphs
} from './ForecastResultsInternalGraphs';
import {
  buildComparisonLegend,
  buildKpiPrimaryOnlyLegend,
  spendPrimaryOnlyLegend
} from './ForecastResultsLegend';
import {
  buildKpiComparisonTooltipRenderer,
  buildKpiPrimaryOnlyTooltipRenderer,
  buildSpendComparisonTooltipRenderer,
  buildSpendPrimaryOnlyTooltipRenderer
} from './ForecastResultsTooltips';

const RIGHT_MARGIN = 16;
const TOP_MARGIN = 20;
const DEFAULT_HEIGHT = '261px';
const Y_AXIS_TICK_COUNT = 6;
const UNDEFINED_AXIS = {
  domain: undefined,
  ticks: undefined
};

interface DataRanges {
  kpiMin: number;
  kpiMax: number;
  kpiEffMin: number;
  kpiEffMax: number;
  spendMin: number;
  spendMax: number;
  cpmMin: number;
  cpmMax: number;
}

interface ForecastResultsChartProps {
  flatForecastData: FlattenedForecastDataResult;
  dataTimestampRanges: ForecastDataTimestampRanges;
  dateGrouping: string;
  syncId?: number;
  height?: string;
  channel: string;
  outcomeVar: OutcomeVariableType;
  kpiName: string;
  selectedKpiView: ForecastKpiViewOption;
  allComparisonBudgets: number[];
  selectedComparisonBudgets: number[];
  earliestVisibleDate: BpmDateString;
  setEarliestVisibleDate: (BpmDateString) => void;
  latestVisibleDate: BpmDateString;
  setLatestVisibleDate: (BpmDateString) => void;
  primaryBudget: number;
}

export const ForecastResultsChart = ({
  flatForecastData,
  dataTimestampRanges,
  dateGrouping,
  syncId,
  height = DEFAULT_HEIGHT,
  channel,
  outcomeVar,
  kpiName,
  selectedKpiView,
  allComparisonBudgets,
  selectedComparisonBudgets,
  earliestVisibleDate,
  setEarliestVisibleDate,
  latestVisibleDate,
  setLatestVisibleDate,
  primaryBudget
}: ForecastResultsChartProps): ReactElement => {
  const [hoveredBudget, setHoveredBudget] = useState<number | undefined>();
  const [selectedBudget, setSelectedBudget] = useState<number | undefined>();
  const [mouseDownTimestamp, setMouseDownTimestamp] = useState<number | undefined>();
  const [mouseMoveTimestamp, setMouseMoveTimestamp] = useState<number | undefined>();
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const kpiChartRef = useRef<HTMLDivElement>(null);

  // Single source of truth for the width that decides monthly vs. fallback x-axis ticks.
  // Measure the KPI (top) chart: it has only a left y-axis, so its width maps cleanly to the
  // plot area via the `- maxYAxisWidth - RIGHT_MARGIN` math below. The spend chart's div is
  // wider in primary-budget-only mode (it carries a right CPM axis internally instead of the
  // external spacer), so measuring it would overestimate the plot width.
  const renderedChartWidth = useContainerSize(kpiChartRef, { widthOnly: true })?.width ?? 0;

  const { trainTestData, historicalData, forecastResultsData } = flatForecastData;
  const combinedData = useMemo(
    () => [...trainTestData, ...historicalData, ...forecastResultsData],
    [forecastResultsData, historicalData, trainTestData]
  );

  const visibleBudgets = useMemo(
    () => (selectedBudget ? [selectedBudget] : selectedComparisonBudgets),
    [selectedBudget, selectedComparisonBudgets]
  );
  const primaryBudgetOnly = useMemo(
    () => selectedComparisonBudgets.length === 0,
    [selectedComparisonBudgets.length]
  );

  const earliestVisibleTimestamp = useMemo(
    () => dateStringToUtcTimestamp(earliestVisibleDate),
    [earliestVisibleDate]
  );

  const latestVisibleTimestamp = useMemo(
    () => dateStringToUtcTimestamp(latestVisibleDate),
    [latestVisibleDate]
  );

  const dataRanges = useMemo<DataRanges | undefined>(() => {
    const filterByTimestamp = row =>
      row.timestamp >= earliestVisibleTimestamp && row.timestamp <= latestVisibleTimestamp;
    const trainTestDataWithinDomain = trainTestData.filter(filterByTimestamp);
    const forecastResultsDataWithinDomain = forecastResultsData.filter(filterByTimestamp);

    let kpiMin = Infinity;
    let kpiEffMin = Infinity;
    let spendMin = Infinity;
    let cpmMin = Infinity;
    let kpiMax = 0;
    let kpiEffMax = 0;
    let spendMax = 0;
    let cpmMax = 0;

    const budgets = primaryBudgetOnly ? [primaryBudget] : selectedComparisonBudgets;

    const VALID_DATA_THRESHOLD = 0.05;

    trainTestDataWithinDomain.forEach(row => {
      const kpiVal = row[`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES[ForecastKpiViewOption.TOTAL]}`];
      const kpiEffVal =
        row[`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES[ForecastKpiViewOption.EFFICIENCY]}`];
      const spendVal = row[`${HISTORICAL_KEY}-spend`];
      const cpmVal = row[`${HISTORICAL_KEY}-cpm`];

      kpiMin = Math.min(kpiMin, kpiVal);
      kpiMax = Math.max(kpiMax, kpiVal);
      // Don't allow Infinity or NaN values to influence the domain
      if (kpiVal > VALID_DATA_THRESHOLD && spendVal > VALID_DATA_THRESHOLD) {
        kpiEffMin = Math.min(kpiEffMin, kpiEffVal);
        kpiEffMax = Math.max(kpiEffMax, kpiEffVal);
      }
      spendMin = Math.min(spendMin, spendVal);
      spendMax = Math.max(spendMax, spendVal);
      cpmMin = Math.min(cpmMin, cpmVal);
      cpmMax = Math.max(cpmMax, cpmVal);
    });

    // Intentionally don't reference the incomplete gap data, as the values can make efficiency metrics unrealistic
    forecastResultsDataWithinDomain.forEach(row => {
      budgets.forEach(budget => {
        const kpiVal = row[`${budget}-${KPI_VIEW_SUFFICES[ForecastKpiViewOption.TOTAL]}`];
        const kpiCiVal = row[`${budget}-${KPI_CI_VIEW_SUFFICES[ForecastKpiViewOption.TOTAL]}`];
        const kpiEffVal = row[`${budget}-${KPI_VIEW_SUFFICES[ForecastKpiViewOption.EFFICIENCY]}`];
        const kpiEffCiVal =
          row[`${budget}-${KPI_CI_VIEW_SUFFICES[ForecastKpiViewOption.EFFICIENCY]}`];
        const spendVal = row[`${budget}-spend`];
        const cpmVal = row[`${budget}-cpm`];

        kpiMin = Math.min(kpiMin, kpiVal, kpiCiVal[0], kpiCiVal[1]);
        kpiMax = Math.max(kpiMax, kpiVal, kpiCiVal[0], kpiCiVal[1]);
        // Don't allow Infinity or NaN values to influence the domain
        if (kpiVal > VALID_DATA_THRESHOLD && spendVal > VALID_DATA_THRESHOLD) {
          kpiEffMin = Math.min(kpiEffMin, kpiEffVal, kpiEffCiVal[0], kpiEffCiVal[1]);
          kpiEffMax = Math.max(kpiEffMax, kpiEffVal, kpiEffCiVal[0], kpiEffCiVal[1]);
        }
        spendMin = Math.min(spendMin, spendVal);
        spendMax = Math.max(spendMax, spendVal);
        cpmMin = Math.min(cpmMin, cpmVal);
        cpmMax = Math.max(cpmMax, cpmVal);
      });
    });

    return {
      kpiMin,
      kpiMax,
      kpiEffMin,
      kpiEffMax,
      spendMin,
      spendMax,
      cpmMin,
      cpmMax
    };
  }, [
    earliestVisibleTimestamp,
    forecastResultsData,
    latestVisibleTimestamp,
    primaryBudget,
    primaryBudgetOnly,
    selectedComparisonBudgets,
    trainTestData
  ]);

  const { domain: kpiDomain, ticks: kpiTicks } = useMemo(() => {
    if (!dataRanges) {
      return UNDEFINED_AXIS;
    }

    const { kpiMin, kpiMax } = dataRanges;
    return buildEquidistantScale(kpiMin, kpiMax, Y_AXIS_TICK_COUNT);
  }, [dataRanges]);

  const { domain: kpiEffDomain, ticks: kpiEffTicks } = useMemo(() => {
    if (!dataRanges) {
      return UNDEFINED_AXIS;
    }

    const { kpiEffMin, kpiEffMax } = dataRanges;
    return buildEquidistantScale(kpiEffMin, kpiEffMax, Y_AXIS_TICK_COUNT);
  }, [dataRanges]);

  const { domain: kpiDomainToUse, ticks: kpiTicksToUse } = useMemo(() => {
    switch (selectedKpiView) {
      case ForecastKpiViewOption.TOTAL:
        return { domain: kpiDomain, ticks: kpiTicks };
      case ForecastKpiViewOption.EFFICIENCY:
        return { domain: kpiEffDomain, ticks: kpiEffTicks };
      default:
        return UNDEFINED_AXIS;
    }
  }, [kpiDomain, kpiEffDomain, kpiEffTicks, kpiTicks, selectedKpiView]);

  const { domain: spendDomain, ticks: spendTicks } = useMemo(() => {
    if (!dataRanges) {
      return UNDEFINED_AXIS;
    }

    const { spendMin, spendMax } = dataRanges;
    return buildEquidistantScale(spendMin, spendMax, Y_AXIS_TICK_COUNT);
  }, [dataRanges]);

  const { domain: cpmDomain, ticks: cpmTicks } = useMemo(() => {
    if (!dataRanges) {
      return UNDEFINED_AXIS;
    }

    const { cpmMin, cpmMax } = dataRanges;
    return buildEquidistantScale(cpmMin, cpmMax, Y_AXIS_TICK_COUNT);
  }, [dataRanges]);

  const { yAxisWidth: topYAxisWidth, setChartRef: setTopChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: combinedData
  });

  const { yAxisWidth: bottomYAxisWidth, setChartRef: setBottomChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: combinedData
  });

  const maxYAxisWidth = useMemo(
    () => Math.max(topYAxisWidth, bottomYAxisWidth),
    [bottomYAxisWidth, topYAxisWidth]
  );

  const colorMap = useMemo(
    () => ({
      ...getColorMap(allComparisonBudgets.map(budget => ({ name: String(budget) }))),
      [primaryBudget]: Brand70
    }),
    [allComparisonBudgets, primaryBudget]
  );

  const kpiPrimaryOnlyGraphs = useMemo(
    () => buildKpiPrimaryOnlyGraphs(primaryBudget, selectedKpiView),
    [primaryBudget, selectedKpiView]
  );

  const kpiComparisonGraphs = useMemo(
    () =>
      buildKpiComparisonGraphs(
        visibleBudgets,
        hoveredBudget,
        selectedBudget,
        colorMap,
        selectedKpiView
      ),
    [visibleBudgets, hoveredBudget, selectedBudget, colorMap, selectedKpiView]
  );

  const spendPrimaryOnlyGraphs = useMemo(
    () => buildSpendPrimaryOnlyGraphs(primaryBudget),
    [primaryBudget]
  );

  const spendComparisonGraphs = useMemo(
    () => buildSpendComparisonGraphs(visibleBudgets, hoveredBudget, colorMap),
    [colorMap, hoveredBudget, visibleBudgets]
  );

  const kpiViewLabel = formatForecastViewLabel(outcomeVar, kpiName, selectedKpiView);

  const kpiPrimaryOnlyLegend = useMemo(
    () => buildKpiPrimaryOnlyLegend(kpiViewLabel),
    [kpiViewLabel]
  );

  const kpiComparisonLegend = useMemo(
    () =>
      buildComparisonLegend({
        title: `Forecasted ${kpiViewLabel}`,
        visibleBudgets,
        primaryBudget,
        colorMap,
        includeCiIfApplicable: true,
        hoveredBudget,
        setHoveredBudget,
        selectedBudget,
        setSelectedBudget
      }),
    [colorMap, hoveredBudget, kpiViewLabel, primaryBudget, selectedBudget, visibleBudgets]
  );

  const spendComparisonLegend = useMemo(
    () =>
      buildComparisonLegend({
        title: 'Forecasted Spend',
        visibleBudgets,
        primaryBudget,
        colorMap,
        hoveredBudget,
        setHoveredBudget,
        selectedBudget,
        setSelectedBudget
      }),
    [colorMap, hoveredBudget, primaryBudget, selectedBudget, visibleBudgets]
  );

  const kpiPrimaryOnlyTooltipRenderer = useMemo(
    () =>
      buildKpiPrimaryOnlyTooltipRenderer({
        dataTimestampRanges,
        dateGrouping,
        fiscalCal,
        primaryBudget,
        outcomeVar,
        kpiName,
        kpiView: selectedKpiView
      }),
    [
      dateGrouping,
      dataTimestampRanges,
      fiscalCal,
      kpiName,
      outcomeVar,
      primaryBudget,
      selectedKpiView
    ]
  );

  const spendPrimaryOnlyTooltipRenderer = useMemo(
    () =>
      buildSpendPrimaryOnlyTooltipRenderer({
        dataTimestampRanges,
        dateGrouping,
        fiscalCal,
        primaryBudget,
        outcomeVar,
        kpiName,
        kpiView: selectedKpiView
      }),
    [
      dateGrouping,
      dataTimestampRanges,
      fiscalCal,
      kpiName,
      outcomeVar,
      primaryBudget,
      selectedKpiView
    ]
  );

  const kpiComparisonTooltipRenderer = useMemo(
    () =>
      buildKpiComparisonTooltipRenderer({
        dataTimestampRanges,
        dateGrouping,
        fiscalCal,
        selectedBudget,
        visibleBudgets,
        colorMap,
        outcomeVar,
        kpiName,
        kpiView: selectedKpiView,
        primaryBudget
      }),
    [
      colorMap,
      dateGrouping,
      dataTimestampRanges,
      fiscalCal,
      kpiName,
      outcomeVar,
      primaryBudget,
      selectedBudget,
      selectedKpiView,
      visibleBudgets
    ]
  );

  const spendComparisonTooltipRenderer = useMemo(
    () =>
      buildSpendComparisonTooltipRenderer({
        dataTimestampRanges,
        dateGrouping,
        fiscalCal,
        selectedBudget,
        visibleBudgets,
        colorMap,
        primaryBudget
      }),
    [
      colorMap,
      dateGrouping,
      dataTimestampRanges,
      fiscalCal,
      primaryBudget,
      selectedBudget,
      visibleBudgets
    ]
  );

  const formatAxisKpi = getKpiAxisFormatter(outcomeVar, selectedKpiView);

  const zoom = () => {
    if (!mouseDownTimestamp || !mouseMoveTimestamp || mouseDownTimestamp === mouseMoveTimestamp) {
      setMouseDownTimestamp(undefined);
      setMouseMoveTimestamp(undefined);
      return;
    }

    // Convert the timestamps to BpmDateStrings
    const earlierTimestamp = Math.min(mouseDownTimestamp, mouseMoveTimestamp);
    const laterTimestamp = Math.max(mouseDownTimestamp, mouseMoveTimestamp);
    const earlierDate = format(
      convertDateToUTC(new Date(earlierTimestamp)),
      'yyyy-MM-dd'
    ) as BpmDateString;
    const laterDate = format(
      convertDateToUTC(new Date(laterTimestamp)),
      'yyyy-MM-dd'
    ) as BpmDateString;

    // Set the min and max visible dates
    setEarliestVisibleDate(earlierDate);
    setLatestVisibleDate(laterDate);

    // Reset the mouse down and move
    setMouseDownTimestamp(undefined);
    setMouseMoveTimestamp(undefined);
  };

  const cpmAxisFormatter = useMemo(() => {
    if (!cpmDomain) {
      return formatCpm;
    }

    const diff = cpmDomain[1] - cpmDomain[0];
    return diff / Y_AXIS_TICK_COUNT > 1 ? formatCpmRounded : formatCpm;
  }, [cpmDomain]);

  // Drive the monthly-vs-fallback x-axis decision from the ResizeObserver-measured container
  // width rather than a DOM measurement of the rendered axis. The latter races with recharts v3's
  // asynchronous rendering, which caused the axis to intermittently revert to the fallback ticks.
  const xAxisPlotWidth = Math.max(0, renderedChartWidth - maxYAxisWidth - RIGHT_MARGIN);

  const { shouldRenderFallback: shouldRenderFallbackAxis, component: monthlyXAxis } =
    useBuildMonthlyXAxis({
      data: combinedData,
      yAxisWidth: maxYAxisWidth,
      domain: [earliestVisibleTimestamp, latestVisibleTimestamp],
      axisWidth: xAxisPlotWidth
    });

  const xAxisComponent = shouldRenderFallbackAxis ? (
    <XAxis
      tick={TICK_STYLE}
      tickFormatter={val => dateToMddyy(parseEpochDate(val))}
      type='number'
      dataKey='timestamp'
      domain={[earliestVisibleTimestamp, latestVisibleTimestamp]}
      scale='time'
      allowDataOverflow
    />
  ) : (
    monthlyXAxis
  );

  const sharedComposedChartProps = {
    data: combinedData,
    margin: { top: TOP_MARGIN, right: RIGHT_MARGIN, left: 0, bottom: 0 },
    syncId,
    onMouseDown: e => {
      if (e?.activeLabel) {
        setMouseDownTimestamp(Number(e.activeLabel));
      }
    },
    onMouseMove: e => {
      if (e?.activeLabel && mouseDownTimestamp) {
        setMouseMoveTimestamp(Number(e.activeLabel));
      }
    },
    onMouseUp: zoom
  };

  const sharedYAxisProps = {
    type: 'number' as 'number' | 'category',
    tick: TICK_STYLE,
    tickLine: false,
    axisLine: false,
    width: maxYAxisWidth,
    scale: 'linear' as ScaleType,
    allowDataOverflow: true
  };

  const sharedCartesianGridProps = {
    stroke: Neutral500,
    strokeOpacity: 0.25,
    strokeWidth: CARTESIAN_GRID_STROKE_WIDTH,
    vertical: false
  };

  return (
    <div className={cn('forecast-results-graph-container', { 'drag-active': mouseDownTimestamp })}>
      <div className='pattern-definitions'>
        <ShadedPattern
          color='white'
          backgroundColor={Brand50}
          id={`Neg${Brand50}`}
          visibleTag={false}
        />
      </div>
      <div className='forecasted-kpis-wrapper'>
        <div
          className='forecast-results-graph forecasted-kpis-graph'
          style={{ height }}
          ref={kpiChartRef}
        >
          <div className='forecast-results-graph-title forecasted-kpis-graph-title'>
            {channel} {kpiViewLabel}
          </div>
          <AutoSizer
            renderProp={({ width, height: chartHeight }) => (
              <ComposedChart
                {...sharedComposedChartProps}
                width={width ?? 0}
                height={chartHeight ?? 0}
                ref={setTopChartRef}
              >
                {xAxisComponent}
                <YAxis
                  {...sharedYAxisProps}
                  tickFormatter={formatAxisKpi}
                  domain={kpiDomainToUse}
                  ticks={kpiTicksToUse}
                />
                {primaryBudgetOnly ? kpiPrimaryOnlyGraphs : kpiComparisonGraphs}
                <CartesianGrid {...sharedCartesianGridProps} />
                {mouseDownTimestamp && mouseMoveTimestamp && (
                  <ReferenceArea
                    x1={mouseDownTimestamp}
                    x2={mouseMoveTimestamp}
                    strokeOpacity={0.3}
                  />
                )}
                <Tooltip
                  content={({ label, payload }) =>
                    selectedComparisonBudgets.length > 0
                      ? kpiComparisonTooltipRenderer({
                          timestamp: Number(label),
                          payload
                        })
                      : kpiPrimaryOnlyTooltipRenderer({
                          timestamp: Number(label),
                          payload
                        })
                  }
                  cursor={{ stroke: Neutral500, width: '1px' }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            )}
          />
        </div>
        <div className='right-y-axis-spacer' style={{ width: maxYAxisWidth }} />
        <div className='right-of-chart'>
          {primaryBudgetOnly ? kpiPrimaryOnlyLegend : kpiComparisonLegend}
        </div>
      </div>
      <div className='forecasted-spend-wrapper'>
        <div className='forecast-results-graph forecasted-spend-graph' style={{ height }}>
          <div className='forecast-results-graph-title forecasted-spend-graph-title'>
            {channel} Spend
          </div>
          <AutoSizer
            renderProp={({ width, height: chartHeight }) => (
              <ComposedChart
                {...sharedComposedChartProps}
                width={width ?? 0}
                height={chartHeight ?? 0}
                ref={setBottomChartRef}
              >
                {xAxisComponent}
                <YAxis
                  {...sharedYAxisProps}
                  yAxisId={0}
                  tickFormatter={formatAxisSpend}
                  domain={spendDomain}
                  ticks={spendTicks}
                />
                {primaryBudgetOnly && (
                  <YAxis
                    {...sharedYAxisProps}
                    yAxisId={1}
                    orientation='right'
                    tickFormatter={cpmAxisFormatter}
                    domain={cpmDomain}
                    ticks={cpmTicks}
                    label={{ value: 'CPM', offset: 20, position: 'top' }}
                  />
                )}
                {primaryBudgetOnly ? spendPrimaryOnlyGraphs : spendComparisonGraphs}
                <CartesianGrid {...sharedCartesianGridProps} />
                {mouseDownTimestamp && mouseMoveTimestamp && (
                  <ReferenceArea
                    x1={mouseDownTimestamp}
                    x2={mouseMoveTimestamp}
                    strokeOpacity={0.3}
                  />
                )}
                <Tooltip
                  content={({ label, payload }) =>
                    selectedComparisonBudgets.length > 0
                      ? spendComparisonTooltipRenderer({
                          timestamp: Number(label),
                          payload
                        })
                      : spendPrimaryOnlyTooltipRenderer({
                          timestamp: Number(label),
                          payload
                        })
                  }
                  cursor={{ stroke: Neutral500, width: '1px' }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            )}
          />
        </div>
        {selectedComparisonBudgets.length !== 0 && (
          <div id='right-y-axis-spacer' style={{ width: maxYAxisWidth }} />
        )}
        <div className='right-of-chart'>
          {primaryBudgetOnly ? spendPrimaryOnlyLegend : spendComparisonLegend}
        </div>
      </div>
    </div>
  );
};
